import { ComponentProps, forwardRef, useMemo } from 'react'
import classNames from 'classnames'
import { Override } from 'maeng-type/Common'

export type ButtonProps = Override<
  ComponentProps<'button'>,
  {
    size?: 'sx' | 'sm' | 'md' | 'lg'
    fullWidth?: boolean
    wide?: boolean
    color?:
      | 'neutral'
      | 'primary'
      | 'secondary'
      | 'accent'
      | 'info'
      | 'success'
      | 'warning'
      | 'error'
    type?: 'ghost' | 'outline' | 'link'
    htmlType?: 'button' | 'submit' | 'reset'
    active?: boolean
    glass?: boolean
    noAnimation?: boolean
    circle?: boolean
    square?: boolean
  }
>

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = 'md',
      color,
      type,
      active,
      disabled,
      glass,
      noAnimation,
      fullWidth,
      wide,
      circle,
      square,
      htmlType = 'button',
      className: classProps = '',
      ...props
    },
    ref,
  ) => {
    const className = useMemo(() => {
      const sizes = {
        'btn-sx': size === 'sx',
        'btn-sm': size === 'sm',
        'btn-lg': size === 'lg',
        'btn-wide': wide,
        'btn-block': fullWidth,
      }
      const colors = {
        'btn-neutral': color === 'neutral',
        'btn-primary': color === 'primary',
        'btn-secondary': color === 'secondary',
        'btn-accent': color === 'accent',
        'btn-info': color === 'info',
        'btn-success': color === 'success',
        'btn-warning': color === 'warning',
        'btn-error': color === 'error',
      }
      const types = {
        'btn-ghost': type === 'ghost',
        'btn-outline': type === 'outline',
        'btn-link': type === 'link',
      }
      const status = {
        'btn-disabled': disabled && !active,
        'btn-active': !disabled && active,
      }
      const effects = {
        glass,
        'no-animation': noAnimation,
      }
      const shapes = {
        'btn-circle': circle && !square,
        'btn-square': square && !circle,
      }
      return classNames({
        btn: true,
        'focus:shadow-none': true,
        ...sizes,
        ...colors,
        ...types,
        ...status,
        ...effects,
        ...shapes,
        [classProps]: true,
      })
    }, [
      size,
      wide,
      fullWidth,
      color,
      type,
      disabled,
      active,
      glass,
      noAnimation,
      circle,
      square,
      classProps,
    ])

    return <button ref={ref} className={className} type={htmlType} {...props} />
  },
)

export default Button
